import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Constellation,
  Walkthrough,
  type ConstellationNode,
  type WalkStep,
} from '@diwata/ironvale'
import { AE, EGO } from '@/lib/graphColors'
import {
  DEMO_STUB_WORLDS,
  demoDomainColors,
  demoStub,
  egoIdOf,
  fetchDemoWorld,
  fetchDemoWorlds,
  personaRole,
  relationsOf,
  toConstellationEdges,
  toConstellationNodes,
  type DemoNode,
  type DemoWorld,
  type DemoWorldSummary,
} from '@/api/demo'
import './explore.css'

// Explore — the PUBLIC /demo route (no auth). A visitor wanders one persona's
// PUBLISHED world read-only: the ironvale constellation as the centrepiece, a
// domain legend to focus, read-only peeks on any star, a glance at "what's
// asking", and a guided tour that auto-offers on first load. Nothing here
// mutates — there are no gates, no edit affordances, no action buttons; the CTA
// simply invites the visitor to build their own.

const MAX_HOP = 4
const TOUR_SEEN_KEY = 'diwa.demo.tour.seen.v1'

/** Injectable world for the render harness / tests; falls back to a fetch. */
export interface ExploreProps {
  world?: DemoWorld
}

export function Explore({ world: injected }: ExploreProps = {}) {
  // The picker menu + which world is selected. Empty menu → single injected world.
  const [worlds, setWorlds] = useState<DemoWorldSummary[]>([])
  const [activeKey, setActiveKey] = useState<string | null>(null)
  const [world, setWorld] = useState<DemoWorld | null>(injected ?? null)
  const [loadFailed, setLoadFailed] = useState(false)
  // The picked world failed to load and this build has no stub for it (e.g. not seeded
  // where the page is served) — say so rather than show a different world.
  const [unavailable, setUnavailable] = useState(false)
  // Lit domains — empty means "the whole connected sky". A read-only focus.
  const [focus, setFocus] = useState<Set<string>>(new Set())
  const [selected, setSelected] = useState<DemoNode | null>(null)
  const [tourOpen, setTourOpen] = useState(false)

  // Load the picker menu. On failure we fall back to the seeded stub worlds so the
  // picker still offers both worlds (never a broken empty page).
  useEffect(() => {
    if (injected) return
    let alive = true
    fetchDemoWorlds()
      .then((ws) => {
        if (!alive) return
        const list = ws.length ? ws : DEMO_STUB_WORLDS
        setWorlds(list)
        setActiveKey((k) => k ?? list[0]?.key ?? null)
      })
      .catch(() => {
        if (!alive) return
        setLoadFailed(true)
        setWorlds(DEMO_STUB_WORLDS)
        setActiveKey((k) => k ?? DEMO_STUB_WORLDS[0]?.key ?? null)
      })
    return () => {
      alive = false
    }
  }, [injected])

  // Load the SELECTED world's full projection whenever the key changes. On any failure
  // we fall back to that world's seeded stub, so the demo is always a live example.
  useEffect(() => {
    if (injected || !activeKey) return
    let alive = true
    setWorld(null)
    setUnavailable(false)
    fetchDemoWorld(activeKey)
      .then((w) => alive && setWorld(w))
      .catch(() => {
        if (!alive) return
        setLoadFailed(true)
        const stub = demoStub(activeKey)
        if (stub) setWorld(stub)
        else setUnavailable(true)
      })
    return () => {
      alive = false
    }
  }, [injected, activeKey])

  // Switch worlds — clear the read-only peek + focus so the new sky starts clean.
  const selectWorld = useCallback(
    (key: string) => {
      setActiveKey((cur) => {
        if (cur === key) return cur
        setSelected(null)
        setFocus(new Set())
        return key
      })
    },
    [],
  )

  const colors = useMemo(() => (world ? demoDomainColors(world) : new Map<string, string>()), [world])
  const egoId = useMemo(() => (world ? egoIdOf(world) : null), [world])
  const nodesById = useMemo(
    () => new Map((world?.graph.nodes ?? []).map((n) => [n.id, n])),
    [world],
  )
  const cnodes = useMemo(
    () => (world ? toConstellationNodes(world, colors, focus) : []),
    [world, colors, focus],
  )
  const cedges = useMemo(() => (world ? toConstellationEdges(world) : []), [world])
  const relations = useMemo(
    () => (world && selected ? relationsOf(world, selected.id) : []),
    [world, selected],
  )

  const theme = useMemo(
    () => ({
      ring: AE.gold,
      star: AE.parchment,
      starHalo: AE.ash,
      focusCore: EGO.core,
      focusHalo: EGO.halo,
      label: AE.parchment,
      sector: AE.ash,
    }),
    [],
  )

  const onNodeClick = useCallback(
    (node: ConstellationNode) => {
      const n = nodesById.get(node.id)
      if (n) setSelected(n)
    },
    [nodesById],
  )

  const toggleFocus = useCallback((slug: string) => {
    setFocus((prev) => {
      const next = new Set(prev)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })
  }, [])

  // Open a peek on a representative star (the ego, whose threads cross domains)
  // so the tour's "peek" step always has something to point at.
  const openTour = useCallback(() => {
    const seed = egoId ?? world?.graph.nodes[0]?.id
    if (seed) setSelected(nodesById.get(seed) ?? null)
    setTourOpen(true)
  }, [egoId, world, nodesById])

  // Auto-offer the tour on first load once the sky is on screen.
  const autoOfferedRef = useRef(false)
  useEffect(() => {
    if (!world || autoOfferedRef.current) return
    autoOfferedRef.current = true
    let seen = false
    try {
      seen = localStorage.getItem(TOUR_SEEN_KEY) === '1'
    } catch {
      /* private mode — treat as first visit */
    }
    if (seen) return
    const t = setTimeout(() => openTour(), 900)
    return () => clearTimeout(t)
  }, [world, openTour])

  const closeTour = useCallback(() => {
    setTourOpen(false)
    try {
      localStorage.setItem(TOUR_SEEN_KEY, '1')
    } catch {
      /* ignore */
    }
  }, [])

  // The tour: constellation → legend → cross-domain thread → a peek → what's
  // asking → the CTA. Autoplay-friendly; missing targets are skipped.
  const steps = useMemo<WalkStep[]>(
    () => [
      {
        target: '[data-tour="sky"]',
        title: `${world?.persona.name ?? 'This'}’s world, as a sky`,
        body: 'Every person, company, paper and event this keeper made public is a star. You can hover, zoom, and pan. This is a real, populated Diwa.',
        placement: 'left',
      },
      {
        target: '[data-tour="legend"]',
        title: 'Domains are sectors of sky',
        body: `Each domain${
          world?.domains.length ? ` (${world.domains.map((d) => d.name).join(', ')})` : ''
        } claims a wedge and a colour. Tap a domain to focus just that region.`,
        placement: 'bottom',
      },
      {
        target: '[data-tour="sky"]',
        title: 'Here’s how domains connect',
        body: 'The golden threads are relations. Some cross between domains: a person who authored a paper, a book that shaped a deal. One connected world, not four separate lists.',
        placement: 'left',
      },
      {
        target: '[data-tour="peek"]',
        title: 'Peek at any star',
        body: 'Click a star to see what it is and everything it connects to. Cross-domain threads are marked. It’s read-only here: a look inside, nothing to change.',
        placement: 'left',
      },
      {
        target: '[data-tour="signals"]',
        title: 'What’s asking',
        body: 'Diwa watches these domains and surfaces what changed. In your own world each of these becomes a one-click action; here it’s just a glimpse of the loop.',
        placement: 'left',
      },
      {
        target: '[data-tour="cta"]',
        title: 'Build your own, free',
        body: 'This is one example. Make your own world of domains in minutes, no card required.',
        placement: 'bottom',
      },
    ],
    [world],
  )

  return (
    <div className="dw-explore">
      {/* ── Banner: the read-only framing + the free CTA ── */}
      <div className="dw-explore-banner">
        <span className="dw-explore-live">
          <span className="dw-explore-live-dot" aria-hidden />
          Live example
        </span>
        <div className="dw-explore-banner-copy">
          You’re exploring <strong>{world?.persona.name ?? 'a keeper'}</strong>’s published world ·{' '}
          <span className="dw-explore-ro">read only</span>.
        </div>
        <div className="dw-explore-actions">
          <button type="button" className="dw-explore-tour-btn" onClick={openTour}>
            ▶ Take the tour
          </button>
          <Link to="/login" className="dw-explore-cta" data-tour="cta">
            Build your own, free →
          </Link>
        </div>
      </div>

      {/* ── World picker: prove Diwa is domain-agnostic — pick a world to explore ── */}
      {worlds.length > 1 && (
        <div className="dw-explore-picker" data-tour="picker" role="group" aria-label="Explore a world">
          <span className="dw-explore-picker-label">Explore a world</span>
          <div className="dw-explore-picker-set">
            {worlds.map((w) => {
              const on = w.key === activeKey
              return (
                <button
                  key={w.key}
                  type="button"
                  className="dw-explore-world-btn"
                  aria-pressed={on}
                  data-active={on || undefined}
                  onClick={() => selectWorld(w.key)}
                >
                  <span className="dw-explore-world-mark" aria-hidden>
                    ◈
                  </span>
                  <span className="dw-explore-world-text">
                    <span className="dw-explore-world-name">{w.persona.name}</span>
                    <span className="dw-explore-world-role">{personaRole(w.persona)}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      )}

      {!world ? (
        <div className="dw-explore-state">
          {unavailable ? "This world isn't published here yet. Pick another." : 'Lighting the sky…'}
        </div>
      ) : (
        <>
          <div className="dw-explore-head">
            <span className="dw-explore-persona">{world.persona.name}</span>
            <div className="dw-explore-tagline">{world.persona.tagline}</div>
          </div>

          <div className="dw-explore-body">
            <div className="dw-explore-sky-col">
              {/* Domain legend — read-only focus */}
              <div className="dw-explore-legend" data-tour="legend" role="group" aria-label="Focus a domain">
                <span className="dw-explore-legend-label">domains</span>
                {world.domains.map((d) => {
                  const on = focus.has(d.slug)
                  const hue = colors.get(d.slug) ?? AE.ash
                  return (
                    <button
                      key={d.slug}
                      type="button"
                      className="dw-explore-chip"
                      aria-pressed={on}
                      title={d.description ?? d.name}
                      onClick={() => toggleFocus(d.slug)}
                      style={{
                        border: `1px solid ${hue}`,
                        background: on ? hue : 'transparent',
                      }}
                    >
                      <span
                        className="dw-explore-chip-dot"
                        style={{ background: on ? '#08070b' : hue }}
                        aria-hidden
                      />
                      {d.name}
                    </button>
                  )
                })}
              </div>

              {/* The constellation — the read-only centrepiece */}
              <div className="dw-explore-sky" data-tour="sky">
                <Constellation
                  nodes={cnodes}
                  edges={cedges}
                  egoId={egoId}
                  reach={MAX_HOP}
                  maxHop={MAX_HOP}
                  theme={theme}
                  getGroupColor={(slug) => colors.get(slug)}
                  onNodeClick={onNodeClick}
                  onBackgroundClick={() => setSelected(null)}
                />
              </div>
            </div>

            {/* Read-only rail: the peek + what's asking */}
            <div className="dw-explore-rail">
              <div className="dw-explore-card" data-tour="peek">
                <div className="dw-explore-card-kicker">
                  Peek
                  <span className="dw-explore-ro-tag">◇ read only</span>
                </div>
                {!selected ? (
                  <div className="dw-explore-peek-empty">
                    Click any star to see what it is and everything it connects to.
                  </div>
                ) : (
                  <>
                    <div className="dw-explore-peek-name">{selected.name}</div>
                    <div className="dw-explore-peek-meta">
                      <span>{selected.type}</span>
                      <span aria-hidden>·</span>
                      <span className="dw-explore-peek-domain">
                        <span
                          className="dw-explore-chip-dot"
                          style={{ background: colors.get(selected.domain) ?? AE.ash }}
                          aria-hidden
                        />
                        {world.domains.find((d) => d.slug === selected.domain)?.name ?? selected.domain}
                      </span>
                    </div>
                    {relations.length > 0 && (
                      <>
                        <div className="dw-explore-rel-head">
                          {relations.length} connection{relations.length === 1 ? '' : 's'}
                        </div>
                        {relations.map((r, i) => (
                          <div className="dw-explore-rel" key={`${r.name}-${i}`}>
                            <span className="dw-explore-rel-verb">{r.relation}</span>
                            <span className="dw-explore-rel-name">{r.name}</span>
                            {r.crossDomain && <span className="dw-explore-rel-cross">cross-domain</span>}
                          </div>
                        ))}
                      </>
                    )}
                    <button type="button" className="dw-explore-peek-close" onClick={() => setSelected(null)}>
                      Close peek
                    </button>
                  </>
                )}
              </div>

              {world.signals.length > 0 && (
                <div className="dw-explore-card" data-tour="signals">
                  <div className="dw-explore-card-kicker">
                    What’s asking
                    <span className="dw-explore-ro-tag">◇ read only</span>
                  </div>
                  {world.signals.map((s) => (
                    <div className="dw-explore-signal" key={s.id}>
                      <div className="dw-explore-signal-title">{s.title}</div>
                      <div className="dw-explore-signal-foot">
                        {s.importance && (
                          <span
                            className="dw-explore-signal-imp"
                            style={{ color: s.importance === 'high' ? EGO.halo : AE.gold }}
                          >
                            {s.importance}
                          </span>
                        )}
                        {s.suggested_action && (
                          <span className="dw-explore-signal-action">{s.suggested_action}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {loadFailed && null /* silent stub fallback — the page stays a live example */}

      <Walkthrough
        steps={steps}
        open={tourOpen}
        onClose={closeTour}
        autoplay={{ stepMs: 5000 }}
      />
    </div>
  )
}
